import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DocumentService } from '../common';
import { NotificationService } from '../common/service/notification.service';
import { DocumentManagerImportService } from './document-manager-import.service';
import { ManagedDocument } from '../common/models/document.model';
import { DocumentPageCacheService } from './document-page-cache.service';
import { Document } from '../model';
import { formatJsonDate } from '../util';
import { hashBlob, hashDataUrl } from './document-hash.util';

@Injectable({ providedIn: 'root' })
export class DocumentUploadService {
  private readonly documentService = inject(DocumentService);
  private readonly importer = inject(DocumentManagerImportService);
  private readonly pageCache = inject(DocumentPageCacheService);
  private readonly notification = inject(NotificationService);

  readonly uploadingDocuments = signal<Set<string>>(new Set());

  setUploadingState(documentId: string, uploading: boolean): void {
    this.uploadingDocuments.update((current) => {
      const next = new Set(current);
      if (uploading) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  }

  async uploadDocument(
    document: ManagedDocument,
    options: {
      ensureDocumentType: (doc: ManagedDocument) => Promise<ManagedDocument | undefined>;
      onUploaded: (uploaded: Document) => Promise<void>;
    }
  ): Promise<void> {
    const readyDocument = await options.ensureDocumentType(document);
    if (!readyDocument?.documentTypeId || !readyDocument.documentDate) {
      return;
    }
    this.setUploadingState(document.id, true);
    try {
      const blob = await this.importer.documentToBlob(readyDocument);
      const filename = `${readyDocument.title}.${readyDocument.extension}`;
      const file = new File([blob], filename, { type: readyDocument.mimeType });
      const uploadMetadata = await this.buildUploadHashes(readyDocument);
      const uploaded = await this.documentService.uploadWithDuplicateConfirm(
        file,
        readyDocument.documentTypeId,
        readyDocument.title,
        () => {},
        uploadMetadata
      );
      if (!uploaded) {
        return;
      }
      const formattedDate = formatJsonDate(readyDocument.documentDate);
      if (formattedDate) {
        try {
          await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
          uploaded.date = formattedDate;
        } catch (error) {
          console.error('Failed to update document date', error);
          throw error;
        }
      }
      await this.notification.success('Document_uploaded_to_server_tr');
      await options.onUploaded(uploaded);
    } catch (error) {
      console.error('Failed to upload document', error);
      throw error;
    } finally {
      this.setUploadingState(document.id, false);
    }
  }

  async buildUploadHashes(document: ManagedDocument): Promise<{ fileHash: string; pageHashes?: (string | null)[] }> {
    // fileHash = SHA-256 of raw file bytes (matches backend verification + docs-service cache key)
    const fileHash = await hashBlob(await this.importer.documentToBlob(document));

    // Page cache keys use the dataUrl hash (frontend-only IndexedDB cache)
    let pageHashes: (string | null)[] | undefined;
    if (document.kind === 'pdf') {
      const totalPages = document.pages ?? 0;
      if (totalPages > 0) {
        const pageCacheKey = await this.computeDocumentHash(document);
        const cached = await this.pageCache.listDocumentPageHashes(pageCacheKey);
        pageHashes = Array.from({ length: totalPages }, (_, index) => cached.get(index + 1) ?? null);
      }
    }
    return { fileHash, pageHashes };
  }

  async suggestDocumentType(document: ManagedDocument): Promise<
    | {
        documentTypeId?: string | null;
        documentTypeName?: string | null;
        documentTypeConfidence?: number | null;
      }
    | undefined
  > {
    if (document.documentTypeId) {
      return {
        documentTypeId: document.documentTypeId,
        documentTypeName: document.documentTypeName ?? undefined,
        documentTypeConfidence: undefined,
      };
    }
    try {
      const blob = await this.importer.documentToBlob(document);
      const filename = `${document.title}.${document.extension}`;
      const file = new File([blob], filename, { type: document.mimeType });
      const { fileHash } = await this.buildUploadHashes(document);
      const response = await this.documentService.analyzeDocument(file, fileHash);
      const analysis = response?.analysis;
      if (!analysis?.documentTypeId) {
        return undefined;
      }
      return {
        documentTypeId: analysis.documentTypeId,
        documentTypeName: analysis.documentType ?? undefined,
        documentTypeConfidence: analysis.documentTypeConfidence ?? undefined,
      };
    } catch (error) {
      console.warn('Failed to analyze document type suggestion', error);
      return undefined;
    }
  }

  async computeDocumentHash(document: ManagedDocument): Promise<string> {
    if (document.docHash) {
      return document.docHash;
    }
    if (document.blob) {
      return await hashBlob(document.blob);
    }
    return await hashDataUrl(document.dataUrl!);
  }

  async scheduleCacheCleanup(document: ManagedDocument): Promise<void> {
    if (document.kind !== 'pdf') {
      return;
    }
    try {
      const hash = await this.computeDocumentHash(document);
      const sixtyDaysMs = 60 * 24 * 60 * 60 * 1000;
      await this.pageCache.markDocumentForDeletion(hash, sixtyDaysMs);
    } catch (error) {
      console.warn('Failed to schedule cache cleanup', error);
    }
  }
}
